import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Keyboard, X } from "lucide-react";
import { useEscapeToClose, useAutoFocusOnOpen } from "@/lib/a11y";

// Two-key sequences (Gmail-style). Pressing `g`, then within 1.2 s
// pressing the second key, navigates to the route. The first keystroke
// arms the leader and the indicator briefly appears in the corner.
const LEADER_TIMEOUT_MS = 1200;
const GOTO_BINDINGS = {
  h: { to: "/", label: "Home / Dashboard" },
  c: { to: "/competitions", label: "Competitions" },
  r: { to: "/results", label: "Results" },
  s: { to: "/stats", label: "Statistics" },
  p: { to: "/photographers", label: "Photographers" },
  a: { to: "/awards", label: "Season Stats" },
  u: { to: "/users", label: "Users" },
};

// True when the active element is something we shouldn't intercept
// keystrokes from (typing into a field, focused select, contenteditable).
function isTypingTarget(target) {
  if (!target) return false;
  const tag = (target.tagName || "").toLowerCase();
  if (tag === "input" || tag === "textarea" || tag === "select") return true;
  if (target.isContentEditable) return true;
  return false;
}

/**
 * Global keyboard-shortcut driver + help overlay (press `?` to open).
 * Mounted once inside <Layout/> so it only listens while signed in.
 */
export default function KeyboardShortcuts() {
  const navigate = useNavigate();
  const [helpOpen, setHelpOpen] = useState(false);
  // leader: which two-key sequence we're partway through (e.g. "g").
  // We don't useState because the timer + key handler is throwaway.
  const leaderRef = useRef(null);
  const leaderTimerRef = useRef(null);
  const [leaderHint, setLeaderHint] = useState(null);

  useEffect(() => {
    const onKey = (e) => {
      // Never intercept while the user is typing.
      if (isTypingTarget(e.target)) return;
      // Honour any modifier — let real browser shortcuts through.
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      // `?` opens help. Shift+/ on most layouts produces "?".
      if (e.key === "?" || (e.key === "/" && e.shiftKey)) {
        e.preventDefault();
        setHelpOpen(true);
        return;
      }

      // Two-key g-leader sequence
      if (leaderRef.current === "g") {
        const binding = GOTO_BINDINGS[e.key.toLowerCase()];
        clearTimeout(leaderTimerRef.current);
        leaderRef.current = null;
        setLeaderHint(null);
        if (binding) {
          e.preventDefault();
          navigate(binding.to);
        }
        return;
      }
      if (e.key.toLowerCase() === "g") {
        e.preventDefault();
        leaderRef.current = "g";
        setLeaderHint("g …");
        clearTimeout(leaderTimerRef.current);
        leaderTimerRef.current = window.setTimeout(() => {
          leaderRef.current = null;
          setLeaderHint(null);
        }, LEADER_TIMEOUT_MS);
        return;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      clearTimeout(leaderTimerRef.current);
    };
  }, [navigate]);

  return (
    <>
      {leaderHint && (
        <div
          className="fixed bottom-20 md:bottom-6 right-6 z-[75] mono text-xs px-3 py-1.5 rounded-lg border"
          style={{ background: "#11061d", borderColor: "#3a2a10", color: "#fbbf24" }}
          aria-live="polite"
          data-testid="keyboard-leader-hint"
        >
          {leaderHint}
        </div>
      )}
      {helpOpen && <ShortcutHelpOverlay onClose={() => setHelpOpen(false)} />}
    </>
  );
}

function ShortcutHelpOverlay({ onClose }) {
  useEscapeToClose(onClose);
  const closeRef = useAutoFocusOnOpen();

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.65)" }}
      onClick={onClose}
      data-testid="keyboard-help-backdrop"
    >
      <div
        className="w-full max-w-lg rounded-2xl border shadow-2xl"
        style={{ background: "#0d0d0d", borderColor: "#1f1f1f" }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="keyboard-help-title"
        data-testid="keyboard-help-overlay"
      >
        <div
          className="flex items-center gap-3 px-5 py-4 border-b"
          style={{ borderColor: "#1f1f1f" }}
        >
          <span
            className="inline-flex items-center justify-center w-9 h-9 rounded-full"
            style={{ background: "#11061d", color: "#fbbf24" }}
            aria-hidden="true"
          >
            <Keyboard size={18} />
          </span>
          <div className="flex-1 min-w-0">
            <div className="mono text-[10px] uppercase tracking-[0.3em] text-amber-400">
              Keyboard
            </div>
            <div id="keyboard-help-title" className="text-base font-semibold tracking-tight">
              Shortcuts
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-neutral-400 hover:text-white"
            aria-label="Close"
            data-testid="keyboard-help-close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-5 max-h-[60vh] overflow-y-auto">
          <ShortcutGroup title="General">
            <ShortcutRow keys={["?"]} label="Show this help" />
            <ShortcutRow keys={["Esc"]} label="Close any dialog or sheet" />
          </ShortcutGroup>

          <ShortcutGroup title="Navigate (press g, then…)">
            {Object.entries(GOTO_BINDINGS).map(([k, { label }]) => (
              <ShortcutRow key={k} keys={["g", k]} label={label} />
            ))}
          </ShortcutGroup>
        </div>

        <div className="px-5 py-4 border-t" style={{ borderColor: "#1f1f1f" }}>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            className="ccs-btn-primary w-full justify-center text-sm"
            data-testid="keyboard-help-got-it"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}

function ShortcutGroup({ title, children }) {
  return (
    <section>
      <div className="mono text-[10px] uppercase tracking-[0.3em] text-amber-400 mb-2">
        {title}
      </div>
      <ul className="divide-y divide-neutral-900">{children}</ul>
    </section>
  );
}

function ShortcutRow({ keys, label }) {
  return (
    <li className="flex items-center justify-between py-2 text-sm">
      <span className="text-neutral-200">{label}</span>
      <span className="flex items-center gap-1">
        {keys.map((k, i) => (
          <kbd
            key={i}
            className="mono text-[11px] px-1.5 py-0.5 rounded border"
            style={{
              background: "#1f1f1f",
              borderColor: "#2f2f2f",
              color: "#fbbf24",
              minWidth: 22,
              textAlign: "center",
              display: "inline-block",
            }}
          >
            {k}
          </kbd>
        ))}
      </span>
    </li>
  );
}
